/**
 * Calendar — Monday-first Uzbek month grid for "Mening davomatim" (S6).
 *
 * Days are dual-encoded (colour AND glyph) so they read without literacy:
 *   present → green  ✓   |  open → amber  ½   |  absent → red  ✕
 *   rest    → grey       |  future → faint    |  none → neutral
 * A late/early flag adds a warn dot in the top-right corner.
 *
 * Props:
 *   days       [{ day, weekday_iso, status, late, early }]  — one per calendar
 *              day of the month (the backend already emits absent/rest/future).
 *   monthLabel string   — e.g. 'Iyul 2026' (server-provided; render verbatim).
 *   onPrev     fn       — go to previous month
 *   onNext     fn       — go to next month
 *   canPrev    bool     — false floors the pager at min_month
 *   canNext    bool     — false when is_current_month (no future)
 *   onDayTap   fn(day)  — tapping a day cell (passes the day entry object)
 *   todayDay?  number   — day-of-month to ring as "today" (when current month)
 */
import { WEEKDAY_SHORT, T } from '../lib/i18n.js';

const GLYPH = { present: '✓', open: '½', absent: '✕' };

// A day cell is tappable only when it carries real punch data.
const TAPPABLE = new Set(['present', 'open']);

export default function Calendar({
  days = [],
  monthLabel = '',
  onPrev,
  onNext,
  canPrev = true,
  canNext = true,
  onDayTap,
  todayDay = null,
}) {
  // Build a Monday-first matrix. Leading blanks before the first day = iso-1.
  const cells = [];
  if (days.length) {
    const firstIso = days[0].weekday_iso || 1;
    for (let i = 0; i < firstIso - 1; i++) cells.push(null);
  }
  for (const d of days) cells.push(d);
  // Pad the tail to a full week so the grid stays rectangular.
  while (cells.length % 7 !== 0) cells.push(null);

  const weeks = [];
  for (let i = 0; i < cells.length; i += 7) weeks.push(cells.slice(i, i + 7));

  return (
    <div>
      <div className="pager">
        <button
          className={`ar ${canPrev ? '' : 'off'}`}
          onClick={canPrev ? onPrev : undefined}
          aria-label="Oldingi oy"
        >
          ‹
        </button>
        <span className="m">{monthLabel}</span>
        <button
          className={`ar ${canNext ? '' : 'off'}`}
          onClick={canNext ? onNext : undefined}
          aria-label="Keyingi oy"
        >
          ›
        </button>
      </div>

      <table className="cal">
        <thead>
          <tr>
            {WEEKDAY_SHORT.map((w) => (
              <th key={w}>{w}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {weeks.map((week, wi) => (
            <tr key={wi}>
              {week.map((d, di) => {
                if (!d) {
                  return (
                    <td key={di}>
                      <div className="cell blank" />
                    </td>
                  );
                }
                const flagged = d.late || d.early;
                const isToday = todayDay != null && d.day === todayDay;
                const tappable = TAPPABLE.has(d.status);
                return (
                  <td key={di}>
                    <div
                      className={`cell ${d.status} ${isToday ? 'today' : ''}`}
                      onClick={tappable && onDayTap ? () => onDayTap(d) : undefined}
                      role={tappable ? 'button' : undefined}
                    >
                      {d.day}
                      {GLYPH[d.status] ? (
                        <span className="g">{GLYPH[d.status]}</span>
                      ) : null}
                      {flagged ? <span className="cn">•</span> : null}
                    </div>
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>

      <div className="legend">
        <span><i style={{ background: 'var(--ok-soft)' }} />{T.legend_present}</span>
        <span><i style={{ background: 'var(--warn-soft)' }} />{T.legend_open}</span>
        <span><i style={{ background: 'var(--bad-soft)' }} />{T.legend_absent}</span>
        <span><i style={{ background: 'var(--grey-soft)' }} />{T.legend_rest}</span>
        <span><i style={{ background: 'var(--warn)', borderRadius: '50%' }} />{T.legend_flag}</span>
      </div>
    </div>
  );
}
